<template>
   <ClientOnly>
      <Teleport to="body">
         <Transition
            enter-active-class="transition duration-200 ease-in-out"
            enter-from-class="opacity-0 -translate-x-2"
            enter-to-class="opacity-100 translate-x-0"
            leave-active-class="transition duration-200 ease-in-out"
            leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 -translate-x-2"
         >
            <div
               v-if="open"
               ref="panel"
               class="__shadow-bottom fixed z-50 flex flex-col gap-2 bg-white p-2 max-md:inset-0 md:top-5 md:left-[262px] md:size-[400px] md:rounded-[calc(var(--radius-medium)-12px)] md:border md:border-grey/15"
            >
               <div class="flex items-center justify-between gap-2 px-2">
                  <p class="h-s">{{ $t('common.notifications') }}</p>
                  <Button variant="ghost" icon="close" @click="emit('close')" />
               </div>

               <NoData
                  v-if="!loading && !notifications.length"
                  :text="$t('web.components.sidebar-notifications.empty')"
               />

               <ul class="flex min-h-0 flex-col gap-1 overflow-auto">
                  <li v-for="notification in notifications" :key="notification.id || ''">
                     <div
                        class="__clickable hover:bg-grey/10 flex items-start gap-2 rounded-lg p-2 transition-all"
                        :class="{
                           'bg-primary/5': !notification.is_read,
                           'opacity-60': notification.is_resolved,
                        }"
                        @click="handleClick(notification)"
                     >
                        <div
                           class="mt-1.5 size-2 shrink-0 rounded-full"
                           :class="notification.is_read ? 'bg-transparent' : 'bg-primary'"
                        />
                        <div class="flex min-w-0 flex-col gap-0.5">
                           <p class="p-s" :class="{ 'font-bold': !notification.is_read }">
                              {{ titleOf(notification) }}
                           </p>
                           <p class="p-s text-grey line-clamp-3">{{ bodyOf(notification) }}</p>
                           <div class="flex flex-wrap items-center gap-2">
                              <p class="p-s text-grey opacity-70">
                                 {{ relativeDate(notification.created_at) }}
                              </p>
                              <HighlightedText
                                 v-if="notification.is_resolved"
                                 :uppercase="false"
                                 type="light-grey"
                                 :text="$t('web.components.sidebar-notifications.resolved')"
                              />
                           </div>
                        </div>
                     </div>
                  </li>
               </ul>
            </div>
         </Transition>
      </Teleport>
   </ClientOnly>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core'
import { useReadNotifications } from '~/composables/api/mutations/read-notifications'
import type { useOrganizationNotification } from '~/composables/api/queries/organization-notification'

type OrganizationNotification = NonNullable<
   ReturnType<typeof useOrganizationNotification>['data']['value']
>[number]

type Emit = {
   close: []
}

type Props = {
   notifications: OrganizationNotification[]
   loading?: boolean
   open?: boolean
}

const props = withDefaults(defineProps<Props>(), {})
const emit = defineEmits<Emit>()

const panel = ref<HTMLElement>()

onClickOutside(
   panel,
   () => {
      if (props.open) emit('close')
   },
   { ignore: ['[data-name="notifications-trigger"]'] }
)

const { t } = useI18n()
const { $dayjs } = useNuxtApp()
const router = useRouter()
const localePath = useLocalePath()

const readNotifications = useReadNotifications()

const isTranslated = (notification: OrganizationNotification) =>
   notification.type === 'NTF_CULTIVATION_PLAN_SIGNATURE'

const paramsOf = (notification: OrganizationNotification) => ({
   code: notification.section_code || '',
   name: notification.section_name || '',
   year: notification.cultivation_plan_year ?? '',
   organization: notification.section_organization_name || '',
})

const titleOf = (notification: OrganizationNotification) =>
   isTranslated(notification)
      ? t(notification.title || '', paramsOf(notification))
      : notification.title || ''

const bodyOf = (notification: OrganizationNotification) =>
   isTranslated(notification)
      ? t(notification.body || '', paramsOf(notification))
      : notification.body || ''

const relativeDate = (date?: string | null) => {
   if (!date) return ''
   return $dayjs(date).isBefore($dayjs().add(-1, 'year'))
      ? $dayjs(date).format('DD MMM YYYY')
      : $dayjs(date).fromNow()
}

const navigateTarget = (notification: OrganizationNotification) => {
   const metadata = notification.metadata as { navigateTo?: string } | null
   return metadata?.navigateTo
}

const handleClick = async (notification: OrganizationNotification) => {
   await tryCatch(async () => {
      if (!notification.is_read && notification.id) {
         await readNotifications.mutateAsync({ p_notification_ids: [notification.id] })
      }
      const target = navigateTarget(notification)
      if (target) {
         emit('close')
         await router.push(localePath(target))
      }
   })
}
</script>
